/**
 * TemplateSelector Component
 *
 * Dropdown selector for training templates in the viewer toolbar.
 * Automatically discovers and lists all templates from the data folder.
 */

import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, ChevronRight, FileJson } from "lucide-react";
import { getAllTemplates, TemplateMetadata } from "@/utils/templateLoader";

interface TemplateSelectorProps {
  selectedTemplateId: string | null;
  onTemplateSelect: (templateId: string) => void;
  className?: string;
  placeholder?: string;
  availableTemplates?: TemplateMetadata[];
}

const groupTemplatesByFolder = (templates: TemplateMetadata[]) => {
  const groups = new Map<
    string,
    { folder: TemplateMetadata["folder"]; templates: TemplateMetadata[] }
  >();

  templates.forEach((template) => {
    const existing = groups.get(template.folder.id);
    if (existing) {
      existing.templates.push(template);
      return;
    }

    groups.set(template.folder.id, {
      folder: template.folder,
      templates: [template],
    });
  });

  return Array.from(groups.values()).sort(
    (a, b) =>
      a.folder.order - b.folder.order ||
      a.folder.label.localeCompare(b.folder.label),
  );
};

interface TemplatePickerProps extends TemplateSelectorProps {
  templates: TemplateMetadata[];
  compact?: boolean;
}

const TemplatePicker: React.FC<TemplatePickerProps> = ({
  selectedTemplateId,
  onTemplateSelect,
  className,
  placeholder,
  templates,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const groupedTemplates = useMemo(
    () => groupTemplatesByFolder(templates),
    [templates],
  );

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find((t) => t.id === selectedTemplateId);
  }, [selectedTemplateId, templates]);

  const initialExpandedFolders = useMemo(() => {
    const selectedGroup = selectedTemplate
      ? groupedTemplates.find((group) =>
          group.templates.some(
            (template) => template.id === selectedTemplate.id,
          ),
        )
      : null;

    return [
      selectedGroup?.folder.id ?? groupedTemplates[0]?.folder.id ?? "templates",
    ];
  }, [groupedTemplates, selectedTemplate]);

  const [expandedFolderIds, setExpandedFolderIds] = useState<string[]>(
    initialExpandedFolders,
  );

  const toggleFolder = (folderId: string, isOpen: boolean) => {
    setExpandedFolderIds((current) =>
      isOpen
        ? Array.from(new Set([...current, folderId]))
        : current.filter((id) => id !== folderId),
    );
  };

  const handleSelect = (templateId: string) => {
    onTemplateSelect(templateId);
    setOpen(false);
  };

  if (templates.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <FileJson className="h-4 w-4" />
        <span>No templates found</span>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "justify-between px-3 font-normal",
            compact ? "h-9 w-[250px]" : "h-10 w-[300px]",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <FileJson className="h-4 w-4 shrink-0" />
            <span className={cn("truncate", compact && "text-sm")}>
              {selectedTemplate?.name || placeholder || "Select a template"}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[320px] max-w-[min(560px,calc(100vw-2rem))] p-1"
      >
        <div className="max-h-[420px] overflow-y-auto pr-1">
          <div className="flex items-center justify-between px-2 py-1.5 text-xs text-muted-foreground">
            <span>{templates.length} templates</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="font-medium text-primary hover:underline"
                onClick={() =>
                  setExpandedFolderIds(
                    groupedTemplates.map((group) => group.folder.id),
                  )
                }
              >
                Expand all
              </button>
              <button
                type="button"
                className="font-medium text-primary hover:underline"
                onClick={() => setExpandedFolderIds([])}
              >
                Collapse all
              </button>
            </div>
          </div>

          {groupedTemplates.map(({ folder, templates: folderTemplates }) => {
            const isFolderOpen = expandedFolderIds.includes(folder.id);

            return (
              <Collapsible
                key={folder.id}
                open={isFolderOpen}
                onOpenChange={(nextOpen) => toggleFolder(folder.id, nextOpen)}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm font-semibold hover:bg-accent"
                  >
                    {isFolderOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="min-w-0 flex-1 truncate">
                      {folder.label}
                    </span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {folderTemplates.length}
                    </span>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pb-1">
                  <div className="space-y-1 pl-4">
                    {folderTemplates.map((template) => {
                      const isSelected = template.id === selectedTemplateId;

                      return (
                        <button
                          key={template.id}
                          type="button"
                          className={cn(
                            "flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent",
                            isSelected && "bg-accent text-accent-foreground",
                          )}
                          onClick={() => handleSelect(template.id)}
                        >
                          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                            {isSelected && <Check className="h-4 w-4" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">
                              {template.name}
                            </span>
                            <span className="line-clamp-2 text-xs text-muted-foreground">
                              {template.description}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplateId,
  onTemplateSelect,
  className,
}) => {
  const templates = useMemo(() => getAllTemplates(), []);

  return (
    <TemplatePicker
      selectedTemplateId={selectedTemplateId}
      onTemplateSelect={onTemplateSelect}
      className={className}
      templates={templates}
    />
  );
};

/**
 * Compact version for toolbar
 */
export const TemplateCompactSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplateId,
  onTemplateSelect,
  className,
  placeholder,
  availableTemplates,
}) => {
  const allTemplates = useMemo(() => getAllTemplates(), []);
  const templates = availableTemplates || allTemplates;

  return (
    <TemplatePicker
      selectedTemplateId={selectedTemplateId}
      onTemplateSelect={onTemplateSelect}
      className={className}
      placeholder={placeholder || "Select template"}
      templates={templates}
      compact
    />
  );
};
